import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';

import { useLoginOrSignupTracking } from '../../../track';
import { useGotoRedirect } from '../../../useGotoRedirect';
import { useLoginOrSignup } from '../../../useLoginOrSignup';
import { useLoginUserFlow } from '../../../useLoginUserFlow';
import { useTrackSuccess } from '../../../useTrackSuccess';

type GotoFinishOtpHook = () => Promise<void>;

export function useGotoFinishOtp(): GotoFinishOtpHook {
	const utilityContext = useUtilityContext();
	const [{ email, otp, authMethod }, { setLoading, setError, clearLoading, setFormFieldError }] =
		useLoginOrSignupPageState();
	const { loginByOtp } = useLoginOrSignup();
	const gotoRedirectTo = useGotoRedirect();
	const trackSuccess = useTrackSuccess();
	const { trackLoginError, trackOtpSubmitSuccess, trackOtpSubmitFailure } = useLoginOrSignupTracking();
	const { translate } = useI18n();
	const userFlow = useLoginUserFlow();

	const hasSetPassword = authMethod === 'PASSWORD_SET';

	const gotoFinishOtp = useCallback(async () => {
		setLoading();

		try {
			const response = await loginByOtp({ email, otp }, utilityContext);

			switch (response.status) {
				case 'LOGGED_IN':
					trackOtpSubmitSuccess();
					await trackSuccess({
						newUser: false,
						loginMethod: 'email',
						loginOrJoinMethod: 'email_otp',
						loginType: 'email_login',
						userFlow,
					});
					gotoRedirectTo({ loginOrJoinMethod: 'email_otp' });
					break;
				case 'NOT_FOUND':
					clearLoading();
					trackOtpSubmitFailure('invalid_otp');
					trackLoginError('email_otp', 'invalid_otp', hasSetPassword);
					setFormFieldError('otp', translate('corgi.login_or_signup.otp.error.invalid'));
					break;
				case 'RATE_LIMITED':
					clearLoading();
					trackOtpSubmitFailure('rate_limited');
					trackLoginError('email_otp', 'rate_limited', hasSetPassword);
					setFormFieldError('otp', translate('corgi.login_or_signup.otp.error.rate_limited'));
					break;
				case 'INVALID_LOGIN_METHOD':
					clearLoading();
					trackOtpSubmitFailure('invalid_login_method');
					setError(translate('corgi.login_or_signup.error.login.invalid_login_method'));
					trackLoginError('email_otp', 'invalid_login_method', hasSetPassword);
					break;
				default:
					clearLoading();
					setError(translate('corgi.login_or_signup.error.network'));
					break;
			}
		} catch {
			clearLoading();
			setError(translate('corgi.login_or_signup.error.network'));
		}
	}, [
		setLoading,
		setError,
		clearLoading,
		setFormFieldError,
		email,
		otp,
		hasSetPassword,
		gotoRedirectTo,
		loginByOtp,
		trackSuccess,
		trackLoginError,
		trackOtpSubmitSuccess,
		trackOtpSubmitFailure,
		utilityContext,
		translate,
		userFlow,
	]);

	return gotoFinishOtp;
}
